import { useTranslation } from "react-i18next";
import css from "./Reviews.module.css";
import React, { useState, useRef, useEffect } from "react";
import { Query } from "@syncfusion/ej2-data";
import OpenFileInViewerIcon from "../../assets/icons/OpenInViewer.svg";
import { listOfStatusAvailableService } from "../../services/dataManager";
import DetailsIcon from "@Components/StickySidebarViewer/assets/Details";
import { getDropdownBackground, getReviewsListForEntity, getStatusBtnStyle } from "./ReviewsHelper";
import DropDownListComponent from "../../cubeComponent/form/DropdownListComponent";
import ChangeFileStatusModal from "./ChangeFileStatusModal";
import ToolTipComponent from "../../cubeComponent/html/tooltip";
import { getReviewUrl } from "../../services/utils/helpers";
import _ from "lodash";

function ReviewsListForEntity({ resource, spaceId, includeDefaultReviews = false }) {
  const { t } = useTranslation();
  const defaultState = { show: false, data: null };
  const [reviewsList, setReviewsList] = useState([]);
  const [signatureModal, setSignatureModal] = useState(defaultState);
  const statusRefs = useRef([]);

  const dataBound = (ref, value) => {
    if (ref && ref.current) {
      ref.current.value = value;
    }
  };

  const closeSignatureModal = () => {
    setSignatureModal(defaultState);
    fetchReviewsListForEntity();
  };

  const openReview = (e) => {
    const projectId = e.currentTarget.dataset.projectid;
    const reviewId = e.currentTarget.dataset.reviewid;
    const spaceId = e.currentTarget.dataset.spaceid;

    const url = getReviewUrl(projectId, spaceId, reviewId);
    window.open(url, "_blank");
  };

  const entityStatusChanged = (e, reviewId, approvalStages) => {
    const statusTo = e.target.itemData;

    const { previousItemData } = e.target;
    const statusFrom = previousItemData || {
      _id: null,
      name: null,
      category: null,
    };

    const currentStage = _.find(approvalStages, {
      clearanceStatus: "in_progress",
    });
    const requireMandatoryComment = currentStage
      ? currentStage.requireMandatoryComment.enabled && currentStage.requireMandatoryComment.values.includes(4)
      : false;

    setSignatureModal({
      show: true,
      data: {
        reviewId,
        statusTo,
        statusFrom,
        resources: [resource],
        type: "standardTransition",
        requireMandatoryComment,
      },
    });
  };

  const fetchReviewsListForEntity = async () => {
    const params = { resource, spaceId, includeDefaultReviews };
    const response = await getReviewsListForEntity({ value: { params } });

    if (response.data.status) {
      setReviewsList(response.data.result);
    }
  };

  useEffect(() => {
    fetchReviewsListForEntity();
  }, [resource.fileVersionId, resource.resourceType]);

  const tooltipContentForReviewDetails = (name, serialId, workflowName, currentReviewLevel, reviewStatus) => (
    <div className={css.reviewTooltipContainer}>
      <div className={css.reviewTooltipRow}>
        <p>{t("Review ID")}</p>
        <p className={css.reviewTooltipContent}>{`RE-${serialId}`}</p>
      </div>

      <div className={css.reviewTooltipRow}>
        <p>{t("Review Name")}</p>
        <p className={css.reviewTooltipContent}>{name}</p>
      </div>

      <div className={css.reviewTooltipRow}>
        <p>{t("Review Stage")}</p>
        <p className={css.reviewTooltipContent}>{reviewStatus}</p>
      </div>

      {currentReviewLevel && (
        <div className={css.reviewTooltipRow}>
          <p>{t("Current Level")}</p>
          <p className={css.reviewTooltipContent}>{`${currentReviewLevel.serial}-${currentReviewLevel.title}`}</p>
        </div>
      )}

      <div className={css.reviewTooltipRow}>
        <p>{t("Review flow Used")}</p>
        <p className={css.reviewTooltipContent}>{workflowName}</p>
      </div>
    </div>
  );

  const dropdownItemTemplate = (args) => {
    const style = getStatusBtnStyle(args.category);

    return (
      <div className={css.itemTemplateWrapper}>
        <span style={style} className={css.itemTemplateContainer}>
          {args.name}
        </span>
      </div>
    );
  };

  return (
    <>
      {reviewsList?.length ? (
        <div className="flex flex-col gap-6 overflow-auto h-80">
          {reviewsList?.length
            ? reviewsList.map((review, index) => {
                const {
                  name,
                  status,
                  spaceId,
                  projectId,
                  reviewId,
                  isDefaultReview,
                  serialId,
                  workflow,
                  currentReviewLevel,
                  reviewStatus,
                  approvalStages,
                } = review;
                const category = status.category || null;

                const query = new Query().addParams("reviewId", reviewId).addParams("resource", resource);
                if (!statusRefs.current[index]) {
                  statusRefs.current[index] = React.createRef();
                }

                return (
                  <div key={reviewId} className="flex flex-row items-center justify-between">
                    <div className="flex flex-row gap-1 items-center">
                      <ToolTipComponent
                        position="left"
                        content={() =>
                          tooltipContentForReviewDetails(
                            name,
                            serialId,
                            workflow.name,
                            currentReviewLevel,
                            reviewStatus,
                          )
                        }
                      >
                        <p className={css.fileStatusRowValue}>{name}</p>
                      </ToolTipComponent>
                      {isDefaultReview ? (
                        <ToolTipComponent
                          showTipPointer
                          position="top"
                          content="This is the default review for the issue. The status can be changed but the review can not be accessed"
                        >
                          <DetailsIcon size="24" color="#192048" />
                        </ToolTipComponent>
                      ) : undefined}
                    </div>
                    <div className="flex flex-row gap-6 items-center">
                      <DropDownListComponent
                        ref={statusRefs.current[index]}
                        dataBound={() => dataBound(statusRefs.current[index], status._id)}
                        value={status._id}
                        dataSource={listOfStatusAvailableService}
                        query={query}
                        onChange={(e) => entityStatusChanged(e, reviewId, approvalStages)}
                        itemTemplate={dropdownItemTemplate}
                        fields={{ value: "_id", text: "name" }}
                        buttonType={getDropdownBackground(category)}
                      />
                      <div
                        data-reviewid={reviewId}
                        data-spaceid={spaceId}
                        data-projectid={projectId}
                        onClick={openReview}
                        className={
                          isDefaultReview
                            ? `${css.openReviewIconContainer} ${css.disableIcon}`
                            : `${css.openReviewIconContainer}`
                        }
                      >
                        <img src={OpenFileInViewerIcon} />
                      </div>
                    </div>
                  </div>
                );
              })
            : undefined}
        </div>
      ) : null}
      {signatureModal.show && <ChangeFileStatusModal {...signatureModal.data} closeModal={closeSignatureModal} />}
    </>
  );
}

export default ReviewsListForEntity;
